import { useState, useCallback } from "react"
import { INITIAL_FORM_DATA, createInitialSeason } from "../constants"
import {
  transformTripAssignmentsFromAPI,
  transformLicensesFromAPI,
  transformAllYearAvailability,
  transformSeasonalAvailabilities
} from "../dataTransformers"


export const useStaffForm = () => {
  const [formData, setFormData] = useState(INITIAL_FORM_DATA)
  const [avatarUrl, setAvatarUrl] = useState(null)
  const [licenses, setLicenses] = useState([])
  const [licenseSectionKey, setLicenseSectionKey] = useState(Date.now())
  const [isLicensesExpanded, setIsLicensesExpanded] = useState(false)
  const [selectedDays, setSelectedDays] = useState([])
  const [tripAssignments, setTripAssignments] = useState([])
  const [availabilityIds, setAvailabilityIds] = useState([])
  const [initialAvailabilityType, setInitialAvailabilityType] = useState(null)
  const [seasons, setSeasons] = useState([createInitialSeason()])

  const populateFormWithStaffData = useCallback((staffData) => {
    const profile = staffData.data || staffData

    if (profile.trip_assignments && profile.trip_assignments.length > 0) {
      setTripAssignments(transformTripAssignmentsFromAPI(profile.trip_assignments))
    }

    setFormData({
      avatar: null,
      firstName: profile.first_name || "",
      lastName: profile.last_name || "",
      email: profile.email || "",
      phone: profile.phone_number || "",
      phoneCountryCode: profile.country_code || "US",
      title: profile.title || "",
      permissionRole: profile.role?.id || "",
      description: profile.description || "",
      availability: profile.availabilities?.[0]?.all_year ? "all_year" : "seasonal",
      experiences: profile.trip_assignments?.map((assignment) => assignment.trip_info.id) || []
    })

    if (profile.photo_url) {
      setAvatarUrl(profile.photo_url)
    }

    if (profile.staff_documents && profile.staff_documents.length > 0) {
      setLicenses(transformLicensesFromAPI(profile.staff_documents))
      setIsLicensesExpanded(true)
    }

    if (profile.availabilities && profile.availabilities.length > 0) {
      const allAvailabilityIds = profile.availabilities.map((a) => a.id).filter(Boolean)
      setAvailabilityIds(allAvailabilityIds)

      const firstAvailability = profile.availabilities[0]

      if (firstAvailability.all_year) {
        setInitialAvailabilityType("all_year")
        const { days } = transformAllYearAvailability(firstAvailability)
        setSelectedDays(days)
      } else {
        setInitialAvailabilityType("seasonal")
        setSeasons(transformSeasonalAvailabilities(profile.availabilities))
      }
    }
  }, [])

  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_DATA)
    setAvatarUrl(null)
    setLicenses([])
    setLicenseSectionKey(Date.now())
    setSelectedDays([])
    setAvailabilityIds([])
    setInitialAvailabilityType(null)
    setTripAssignments([])
    setSeasons([createInitialSeason()])
    setIsLicensesExpanded(false)
  }, [])

  const handleImageChange = useCallback(
    (e) => {
      const file = e.target.files[0]
      if (file) {
        const reader = new FileReader()
        reader.addEventListener("load", () => {
          setAvatarUrl(reader.result)
          setFormData((prev) => ({ ...prev, avatar: file }))
        })
        reader.readAsDataURL(file)
      }
    },
    []
  )

  return {
    formData,
    avatarUrl,
    licenses,
    licenseSectionKey,
    isLicensesExpanded,
    selectedDays,
    tripAssignments,
    availabilityIds,
    initialAvailabilityType,
    seasons,
    setFormData,
    setAvatarUrl,
    setLicenses,
    setLicenseSectionKey,
    setIsLicensesExpanded,
    setSelectedDays,
    setTripAssignments,
    setAvailabilityIds,
    setInitialAvailabilityType,
    setSeasons,
    populateFormWithStaffData,
    resetForm,
    handleImageChange
  }
}
